import { useQCTaskDefinitionSlice } from '@restore/QCTaskDefinition/QCTaskDefinition.slice';
import { Button } from '@dynamika-shared/ui/Button/Button';
import {
  TaskDefinitionExecutionNamedStatus,
  TaskDefinitionStatus,
} from '@dynamika-shared/types/qcTaskDefinitions';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { StatusPill } from '@dynamika-shared/ui/StatusPill/StatusPill';
import { ReadingTaskDataCheck } from 'src/pages/QC/QCReadingTypes/components/ReadingTaskDataCheck';
import { Modal } from '@dynamika-shared/ui/Modal/Modal';
import { SkeletonLine } from '@dynamika-shared/ui/Skeleton/Skeleton';

export const QcReadingTypes = () => {
  const [readingTypes, loading, updateQCTaskDefinition] =
    useQCTaskDefinitionSlice((state, actions) => [
      state.data,
      state.loading,
      actions.updateQCTaskDefinition,
    ]);

  const { id: clinicalTrialId } = useParams<{
    id: string;
    taskId: string;
  }>();

  const [showModal, setShowModal] = useState<{
    taskDefinitionId: string;
    status: TaskDefinitionStatus;
  } | null>(null);

  const handleGenerate = () => {
    if (clinicalTrialId && showModal?.taskDefinitionId) {
      updateQCTaskDefinition({
        clinicalTrialId,
        taskDefinitionId: showModal?.taskDefinitionId,
        comment: '',
      });
    }
    setShowModal(null);
  };

  const loadingSkeleton = (
    <div className='flex items-center text-1624 font-semibold'>
      <SkeletonLine className='!w-full h-5' />
    </div>
  );

  return (
    <div className='flex h-[calc(100vh-400px)] flex-col overflow-auto'>
      {loading
        ? loadingSkeleton
        : readingTypes?.map(
            ({ id, name, executionStatus, status, lastExecutionDate }) => {
              return (
                <div key={id}>
                  <div className='mb-[22px] flex items-center justify-between font-medium rounded-[8px] bg-[#F6F9FF] p-[12px] text-1824 border-[1px] border-brightGray'>
                    <div className='flex gap-1 items-center'>
                      {name}
                      {executionStatus && (
                        <StatusPill
                          status={executionStatus}
                          textSeparator={'_'}
                          textCombiner={' '}
                        />
                      )}
                    </div>

                    <div className='flex items-center gap-1'>
                      <ReadingTaskDataCheck
                        executionStatus={executionStatus}
                        lastExecutionDate={lastExecutionDate}
                        status={status}
                      />
                      {executionStatus !==
                        TaskDefinitionExecutionNamedStatus.COMPLETED && (
                        <Button
                          size='small'
                          className='w-[10rem]'
                          type='primary'
                          disabled={
                            !!readingTypes?.find(
                              ({ executionStatus }) =>
                                executionStatus ===
                                  TaskDefinitionExecutionNamedStatus.IN_PROGRESS ||
                                status === TaskDefinitionStatus.NOT_READY
                            )
                          }
                          onClick={() =>
                            setShowModal({
                              taskDefinitionId: id,
                              status,
                            })
                          }
                        >
                          Ready
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            }
          )}
      <Modal
        width={440}
        onCancel={() => setShowModal(null)}
        onOk={handleGenerate}
        okText={'Create'}
        centered
        cancelText='Cancel'
        maskClosable={false}
        open={!!showModal}
        mask={false}
        classNames={{
          header: 'text-left !mb-3',
          content: 'text-center !rounded-[2.4rem] !p-[4rem]',
          footer: 'flex justify-between !mt-3',
        }}
        title={'Reading Creation'}
        titleClassName='text-left'
      >
        {showModal?.status === TaskDefinitionStatus.NOT_READY
          ? 'Not all expected labels for this reading were assigned, which may prevent the reading from being created. Please confirm that the reading is successfully created after you click on Create.'
          : 'Please confirm if you want to create the reading.'}
      </Modal>
    </div>
  );
};
